import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Pill, Search, FileText, ArrowLeft, Package, AlertTriangle, Banknote, Check, ShoppingCart } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientRecommendedPage = () => {
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [productRecs, setProductRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmRec, setConfirmRec] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/recommended-supplements/my`),
      axios.get(`${API}/store/my-recommendations`),
    ]).then(([supRes, prodRes]) => {
      setRecommendations(supRes.data);
      setProductRecs(prodRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleConfirmOrder = async () => {
    if (!confirmRec) return;
    setConfirming(true);
    try {
      await axios.post(`${API}/store/confirm-recommendation/${confirmRec.id}`);
      toast.success(`Orden de ${confirmRec.product_name} confirmada exitosamente`);
      setConfirmRec(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al confirmar la orden');
    } finally {
      setConfirming(false);
    }
  };

  const filtered = recommendations.filter(r =>
    r.supplement_name.toLowerCase().includes(searchTerm.toLowerCase())
    || (r.supplement_category || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredProducts = productRecs.filter(r =>
    r.product_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalCount = recommendations.length + productRecs.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 rounded-full border-4 border-orange-100 border-t-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-5 animate-fade-in pb-4" data-testid="client-recommended-page">
      <button onClick={() => navigate('/client/supplements')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors" data-testid="back-to-supplements">
        <ArrowLeft size={16} /> Suplementos
      </button>

      <div className="pt-1">
        <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Recomendados
        </h1>
        <p className="text-sm text-gray-400 mt-1 font-medium">Suplementos seleccionados por tu entrenador</p>
      </div>

      {/* Count banner */}
      <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl p-4 text-white shadow-lg shadow-orange-500/20 flex items-center gap-3" data-testid="recommended-count-banner">
        <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
          <FileText size={18} />
        </div>
        <p className="text-sm font-medium">
          {totalCount === 0
            ? 'Aun no tienes suplementos recomendados'
            : `${totalCount} recomendacion${totalCount > 1 ? 'es' : ''}`
          }
        </p>
      </div>

      {/* Search */}
      {totalCount > 0 && (
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <Input
            placeholder="Buscar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-11 bg-white border-gray-100 focus:border-orange-400 focus:ring-2 focus:ring-orange-500/20 rounded-xl h-11 shadow-sm"
            data-testid="search-recommended"
          />
        </div>
      )}

      {/* Pending product recommendations */}
      {filteredProducts.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-bold text-orange-600 uppercase tracking-wider">Ordenes pendientes de confirmar</p>
          {filteredProducts.map((rec, idx) => (
            <div
              key={rec.id}
              className="bg-white rounded-2xl border-2 border-orange-200 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300"
              style={{ animationDelay: `${idx * 60}ms` }}
              data-testid={`product-rec-${rec.id}`}
            >
              <div className="h-1.5 bg-gradient-to-r from-orange-400 via-amber-500 to-orange-400" />
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-50 to-violet-50 flex items-center justify-center flex-shrink-0">
                    <Package size={20} className="text-indigo-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 text-sm">{rec.product_name}</p>
                    <span className="inline-block text-[10px] font-bold uppercase tracking-wide mt-0.5 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600">
                      {rec.product_type === 'semanal' ? '7 dias' : rec.product_type === 'mensual' ? '28 dias' : rec.product_type}
                    </span>
                    <div className="flex items-center justify-between mt-2.5">
                      <p className="text-xs text-gray-400">
                        Enviado por <span className="font-semibold text-gray-600">{rec.distributor_name}</span>
                      </p>
                      <div className="flex items-center gap-2">
                        {rec.quantity > 1 && (
                          <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-lg">x{rec.quantity}</span>
                        )}
                        <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                          RD${(rec.sale_price * rec.quantity).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setConfirmRec(rec)}
                      className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 shadow-md shadow-orange-500/20"
                      data-testid={`confirm-rec-btn-${rec.id}`}
                    >
                      <ShoppingCart size={16} /> Confirmar Orden
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Regular supplement recommendations */}
      {filtered.length === 0 && filteredProducts.length === 0 ? (
        <div className="bg-white rounded-3xl border border-gray-100/50 shadow-sm p-10 text-center" data-testid="no-recommended">
          <div className="w-20 h-20 bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Pill size={32} className="text-orange-300" />
          </div>
          <p className="font-bold text-gray-700 text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {searchTerm ? 'Sin resultados' : 'Sin recomendaciones'}
          </p>
          <p className="text-sm text-gray-400 mt-1">
            {searchTerm ? 'No hay suplementos que coincidan' : 'Tu entrenador aun no te ha recomendado suplementos'}
          </p>
        </div>
      ) : filtered.length > 0 && (
        <div className="space-y-3">
          {filteredProducts.length > 0 && (
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mt-2">Suplementos individuales</p>
          )}
          {filtered.map((rec, idx) => (
            <div
              key={rec.id}
              className="bg-white rounded-2xl border border-gray-100/50 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300"
              style={{ animationDelay: `${idx * 60}ms` }}
              data-testid={`supplement-${rec.id}`}
            >
              <div className="h-1 bg-gradient-to-r from-orange-400 to-amber-500" />
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                    <Pill size={20} className="text-orange-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 text-sm">{rec.supplement_name}</p>
                    {rec.supplement_category && (
                      <span className="inline-block text-[10px] font-bold uppercase tracking-wide mt-0.5 px-2 py-0.5 rounded-full bg-orange-50 text-orange-600">
                        {rec.supplement_category}
                      </span>
                    )}
                    {rec.supplement_description && (
                      <p className="text-xs text-gray-400 mt-1.5 line-clamp-2">{rec.supplement_description}</p>
                    )}
                    {rec.notes && (
                      <div className="mt-2 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                        <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wide mb-0.5">Nota del entrenador</p>
                        <p className="text-xs text-amber-700">{rec.notes}</p>
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-2.5">
                      <p className="text-xs text-gray-400">
                        Recomendado por <span className="font-semibold text-gray-600">{rec.distributor_name}</span>
                      </p>
                      <div className="flex items-center gap-2">
                        {rec.quantity > 1 && (
                          <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-lg">x{rec.quantity}</span>
                        )}
                        {rec.supplement_price > 0 && (
                          <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                            ${rec.supplement_price.toFixed(2)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirm Order Modal */}
      {confirmRec && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setConfirmRec(null)}>
          <div className="bg-white rounded-3xl w-full max-w-sm shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} data-testid="confirm-recommendation-modal">
            <div className="h-1.5 bg-gradient-to-r from-orange-400 via-amber-500 to-orange-400" />
            <div className="p-6">
              <div className="w-14 h-14 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <AlertTriangle size={28} className="text-amber-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 text-center mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Confirmar Orden
              </h3>
              <p className="text-sm text-gray-500 text-center mb-5">
                Tu entrenador te ha recomendado:
              </p>

              <div className="bg-gray-50 rounded-2xl p-4 mb-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Producto</span>
                  <span className="font-bold text-gray-900">{confirmRec.product_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Cantidad</span>
                  <span className="font-bold text-gray-900">{confirmRec.quantity}</span>
                </div>
                <div className="border-t border-gray-200 pt-2 flex justify-between text-sm">
                  <span className="text-gray-500">Total</span>
                  <span className="font-black text-lg text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    RD${(confirmRec.sale_price * confirmRec.quantity).toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200/60 rounded-xl p-3 mb-5">
                <Banknote size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs font-medium text-amber-800 leading-relaxed">
                  Este pedido debe ser pagado en efectivo o transferencia al momento de la entrega.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmRec(null)}
                  className="flex-1 py-2.5 rounded-xl font-bold text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
                  data-testid="cancel-rec-btn"
                >
                  No, cancelar
                </button>
                <button
                  onClick={handleConfirmOrder}
                  disabled={confirming}
                  className="flex-1 py-2.5 rounded-xl font-bold text-sm text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:shadow-lg transition-all disabled:opacity-70"
                  data-testid="confirm-rec-order-btn"
                >
                  {confirming ? 'Procesando...' : 'Si, confirmar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientRecommendedPage;
